import React from 'react';
import styled from 'styled-components';

import { UTM_PARAM_KEYS, UTM_PARAM_VALUES } from '../../../shared/constants';
import { ICard, IContentfulLink, IImage } from '../../../types/shared';

import { createUtmParam } from '../../../utils/string-utils';

import mq from '../../../styles/breakpoints';
import { H3 } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import { useWizardStore } from '../../../state/wizard/Wizard.store';
import { WizardItem } from '../../../types/wizard';
import WizardResultCard from '../../molecules/wizard-result-card/WizardResultCard';

import { parseGroupedResults } from './WizardResultLayout.helpers';

interface WizardResultListProps {
  results: WizardItem[];
  renderCardTitles: boolean;
  hasIframe?: boolean;
  cardElevation?: 'low' | 'high';
}

const iframeUtmParam = createUtmParam(
  UTM_PARAM_KEYS.campaign,
  UTM_PARAM_VALUES.dkpnIframe
);

// TODO: Re-enable this. I have disabled this to make 100% sure we dont render this in the frontend before FSCP golive. (12-02-2025).
const renderBodyItems = (
  bodyItems: WizardItem,
  groupIndex: number,
  cardElevation?: 'low' | 'high',
  hasIframe?: boolean
) => {
  if (bodyItems.visual?.results) {
    return bodyItems.visual.results.map(
      ({ title, body, primaryLink, image }: ICard, index: number) => (
        <StyledListItem key={index}>
          <WizardResultCard
            title={title}
            body={body}
            primaryLink={primaryLink as IContentfulLink}
            image={image as IImage}
            cardNumber={groupIndex + 1}
            cardElevation={cardElevation}
            extraLinkParams={hasIframe ? iframeUtmParam : ''}
          />
        </StyledListItem>
      )
    );
  }
};

const WizardResultList = ({
  results,
  renderCardTitles,
  hasIframe = false,
  cardElevation = 'low',
}: WizardResultListProps) => {
  const { getItem } = useWizardStore();
  // TODO: Maybe make this grouped data structure available in the store. Because it's similair to the structure we use in the contact form.
  const groupedResults = parseGroupedResults(results);

  return (
    <StyledResultList>
      {groupedResults.map((result, groupIndex) => {
        const groupItem = getItem(result.id);

        return (
          <StyledListItem key={groupIndex}>
            {renderCardTitles && <StyledH3>{groupItem.title}</StyledH3>}
            <StyledList>
              {result.items.map((subResult) =>
                renderBodyItems(subResult, groupIndex, cardElevation, hasIframe)
              )}
            </StyledList>
          </StyledListItem>
        );
      })}
    </StyledResultList>
  );
};

const StyledList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const StyledResultList = styled(StyledList)`
  margin: 0 0 ${Spacings.XL};
  padding: 0 ${Spacings.S};

  ${mq.from.breakpoint.M`
    padding: 0;
  `}
`;

const StyledListItem = styled.li`
  margin: 0;
  padding: 0;
`;

const StyledH3 = styled(H3)`
  margin: ${Spacings.M} 0 ${Spacings.S};
`;

export default WizardResultList;
